import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTrip } from '@/services/tripService'
import { listDocuments, createDocument, deleteDocument } from '@/services/documentService'
import {
  newDocumentSchema,
  DOCUMENT_TYPES,
  type NewDocumentFormValues,
  type NewDocumentFormInput,
} from '@/utils/schemas'

const EXPIRING_SOON_DAYS = 90

function expirationStatus(expirationDate: string | null): 'expired' | 'expiringSoon' | null {
  if (!expirationDate) return null
  const daysLeft =
    (new Date(`${expirationDate}T00:00:00Z`).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  if (daysLeft < 0) return 'expired'
  if (daysLeft <= EXPIRING_SOON_DAYS) return 'expiringSoon'
  return null
}

export function DocumentsPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const documentsQuery = useQuery({
    queryKey: ['documents', tripId],
    queryFn: () => listDocuments(tripId!),
    enabled: !!tripId,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewDocumentFormInput, unknown, NewDocumentFormValues>({
    resolver: zodResolver(newDocumentSchema),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents', tripId] })

  const addDocumentMutation = useMutation({
    mutationFn: (values: NewDocumentFormValues) =>
      createDocument({
        tripId: tripId!,
        type: values.type,
        fileUrl: values.fileUrl,
        expirationDate: values.expirationDate,
      }),
    onSuccess: async () => {
      await invalidate()
      reset()
    },
  })

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
    onSuccess: invalidate,
  })

  if (tripQuery.isLoading || documentsQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('documents.loading')}</p>
  }

  const trip = tripQuery.data
  const documents = documentsQuery.data ?? []

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('documents.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('documents.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        {documents.length === 0 && <p className="text-sm text-mist">{t('documents.empty')}</p>}

        {documents.map((document) => {
          const status = expirationStatus(document.expiration_date)
          return (
            <div
              key={document.id}
              className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3"
            >
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <span className="font-sans text-[15px] font-medium text-ivory">
                    {t(`documents.type.${document.type}`)}
                  </span>
                  {status && (
                    <span
                      className={
                        'font-mono text-[10px] tracking-wide uppercase ' +
                        (status === 'expired' ? 'text-brass-warm' : 'text-brass')
                      }
                    >
                      {t(`documents.${status}`)}
                    </span>
                  )}
                </div>
                <a
                  href={document.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-mist hover:text-brass"
                >
                  {document.file_url}
                </a>
                {document.expiration_date && (
                  <span className="font-mono text-xs text-brass-dim">
                    {document.expiration_date}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => deleteDocumentMutation.mutate(document.id)}
                className="font-sans text-xs text-mist hover:text-brass-warm"
              >
                {t('documents.deleteDocument')}
              </button>
            </div>
          )
        })}

        <form
          onSubmit={handleSubmit((values) => addDocumentMutation.mutate(values))}
          className="mt-4 flex flex-col gap-3 rounded-md border border-line-strong bg-panel-2 p-4"
        >
          <select
            aria-label={t('documents.selectType')}
            className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('type')}
          >
            {DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`documents.type.${type}`)}
              </option>
            ))}
          </select>
          <input
            aria-label={t('documents.fileUrl')}
            placeholder={t('documents.fileUrl')}
            className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('fileUrl')}
          />
          {errors.fileUrl && (
            <p role="alert" className="text-sm text-brass-warm">
              {t('documents.fileUrlRequired')}
            </p>
          )}
          <input
            type="date"
            aria-label={t('documents.expirationDate')}
            className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('expirationDate')}
          />
          {addDocumentMutation.isError && (
            <p role="alert" className="text-sm text-brass-warm">
              {t('documents.saveError')}
            </p>
          )}
          <button
            type="submit"
            disabled={addDocumentMutation.isPending}
            className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
          >
            {t('documents.addDocument')}
          </button>
        </form>
      </div>
    </div>
  )
}
